import React, { useEffect, useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  CircularProgress, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { getPhotographers, createPhotographer, updatePhotographer, deletePhotographer } from '../services/photographerService';
import type { Photographer } from '../../../shared/types';

export const PhotographerManagement: React.FC = () => {
  const { agency } = useAuth();
  const [photographers, setPhotographers] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPhotographer, setEditingPhotographer] = useState<Photographer | null>(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    photographer: null as Photographer | null
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    specialties: ''
  });

  if (!agency) return null;

  useEffect(() => {
    fetchPhotographers();
  }, []);

  const fetchPhotographers = async () => {
    try {
      setLoading(true);
      const data = await getPhotographers(agency.slug);
      setPhotographers(data);
    } catch (error) {
      console.error('Error fetching photographers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (photographer?: Photographer) => {
    if (photographer) {
      setEditingPhotographer(photographer);
      setFormData({
        name: photographer.name,
        email: photographer.email || '',
        phone: photographer.phone || '',
        website: photographer.website || '',
        specialties: photographer.specialties?.join(', ') || ''
      });
    } else {
      setEditingPhotographer(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        website: '',
        specialties: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPhotographer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      website: '',
      specialties: ''
    });
  };

  const handleSave = async () => {
    try {
      const photographerData = {
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(s => s)
      };

      if (editingPhotographer) {
        await updatePhotographer(agency.slug, editingPhotographer._id, photographerData);
      } else {
        await createPhotographer(agency.slug, photographerData);
      }
      
      await fetchPhotographers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving photographer:', error);
    }
  };

  const handleDeleteClick = (photographer: Photographer) => {
    setConfirmDialog({ open: true, photographer });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog.photographer) return;
    
    try {
      await deletePhotographer(agency.slug, confirmDialog.photographer._id);
      setConfirmDialog({ open: false, photographer: null });
      await fetchPhotographers();
    } catch (error) {
      console.error('Error deleting photographer:', error);
    }
  };

  return (
    <Card sx={{ mb: 4, mt: 4 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Photographer Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Photographer
          </Button>
        </Box>
        
        {loading ? (
          <CircularProgress />
        ) : (
          <List>
            {photographers.map((photographer) => (
              <ListItem key={photographer._id} divider>
                <ListItemText
                  primary={photographer.name}
                  secondary={
                    <>
                      {photographer.email && <span style={{ display: 'block' }}>{photographer.email}</span>}
                      {photographer.phone && <span style={{ display: 'block' }}>{photographer.phone}</span>}
                      {photographer.specialties && photographer.specialties.length > 0 && (
                        <span style={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                          Specialties: {photographer.specialties.join(', ')}
                        </span>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleOpenDialog(photographer)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteClick(photographer)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPhotographer ? 'Edit Photographer' : 'Add New Photographer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              fullWidth
            />
            <TextField
              label="Specialties (comma-separated)"
              value={formData.specialties}
              onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
              fullWidth
              placeholder="e.g., Real Estate, Interior, Architecture"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingPhotographer ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, photographer: null })}
      >
        <DialogTitle>Delete Photographer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{confirmDialog.photographer?.name}</strong>?
          </Typography>
          <Typography color="warning.main" sx={{ mt: 2 }}>
            This will remove the photographer from your agency. If they have a user account, they will no longer be able to access their jobs.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, photographer: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
